import api from './api.services'
import {
  ActivityType,
  AppleHealthDiaryEntry,
  OnboardingStatus,
  ProgressType,
  ServiceProvider,
  UserProfileDto,
} from './api.types'
import { AppleHealthDataTypes } from '../common/integration/integrationSagas'
import moment from 'moment'
import { IGlobalState } from '../Root/store/appReducer'
import { store } from '../index'
import { log } from './log'
import { clearNotification } from '../common/notifications/NotificationActions'
import { isNative } from '../common/helpers/isNative'
import { push } from 'connected-react-router'
import { ROUTES } from '../common/Routes/routePaths'
import { t } from 'ttag'
import { Capacitor } from '@capacitor/core'
import { getWhoAmI } from '../common/auth/authActions'
import { getUserCompanies, getUserDto, getUserIntegrationScopes } from '../common/auth/authSelectors'
import { GoogleFit } from './GoogleFit'
import { ActivityContainer, HistoryActivityData } from './GoogleFitDefinitions'
import { convertUTCToLocalTime } from '../common/helpers/date'
import { Preferences } from '@capacitor/preferences'

export const refreshActivityView = () => {
  store.dispatch(clearNotification())

  // TODO: Migrate this to just updating user profile (as below for Progress view).
  /*let goalId = null
  const globalState = store.getState() as any as IGlobalState
  if (globalState.router && globalState.router.location && globalState.router.location.pathname) {
    const path = globalState.router.location.pathname.split('/')
    if (path[1] === 'activity') {
      goalId = path[2]
    }
  }
  store.dispatch(
    getCurrentCompanyGoal({
      id: goalId,
      silentGoalUpdate: true,
      updateList: true,
    }),
  )*/
  // This triggers Progress view update.
  store.dispatch(getWhoAmI())
}

// Needed for Google Fit Spring 2021 scope changes.
const handleGoogleFitScopeChange = (msg: string) => {
  // Don't bother if it's integration proxy.
  const globalState = store.getState() as any as IGlobalState
  if (globalState.router && globalState.router.location && globalState.router.location.pathname) {
    const path = globalState.router.location.pathname.split('/')
    if (path[1] === 'integration-proxy') return
  }

  // Check for a specific tag.
  if (msg.startsWith('GOOGLE_FIT:')) {
    msg = msg.substr(11).trim()
    if (!isNative()) {
      if (window.confirm(msg)) {
        store.dispatch(push(`${ROUTES.INTEGRATION_PROXY.to}?type=${ServiceProvider.GOOGLE_FIT}&scope=FULL`))
      }
    } else {
      // @ts-ignore
      navigator.notification.confirm(
        msg,
        (button: number) => {
          if (button === 1) return
          store.dispatch(push(`${ROUTES.INTEGRATION_PROXY.to}?type=${ServiceProvider.GOOGLE_FIT}&scope=FULL`))
        },
        `Google Fit`,
        [t`Cancel`, t`OK`],
      )
    }
  }
}

export const dataSync = async (manualSync?: boolean, forceLongSync = false, nonDistanceSync?: boolean) => {
  try {
    const state = (await store.getState()) as any as IGlobalState
    const userDto = await getUserDto(state)
    const { userId, serviceProvider, onboardingStatus, integrationError, dateOfBirth }: UserProfileDto = userDto

    const isGoogleFit = serviceProvider === ServiceProvider.GOOGLE_FIT

    // We need to remove this, as it blocks the sync.
    /*if (integrationError) {
      if (isGoogleFit) {
        return handleGoogleFitScopeChange(integrationError)
      }
    }*/

    if (!userId) {
      return
    }

    if (
      !isNative() ||
      (onboardingStatus !== OnboardingStatus.DONE && !manualSync && !forceLongSync && !nonDistanceSync)
    ) {
      return
    }

    if (
      (Capacitor.getPlatform() === 'ios' && serviceProvider !== ServiceProvider.APPLE_HEALTH) ||
      (Capacitor.getPlatform() === 'android' && !forceLongSync && serviceProvider !== ServiceProvider.GOOGLE_FIT)
    ) {
      return
    }

    const isAppleHealth = serviceProvider === ServiceProvider.APPLE_HEALTH

    // We define the throttle limit in milliseconds: 5 seconds for manual sync and 30 seconds for background sync.
    const appleHealthThrottleLimit = nonDistanceSync ? 0 : manualSync ? 5000 : 30000
    const timestamp = Date.now()
    const { value: lastAppleHealthPushRaw } = await Preferences.get({ key: 'lastAppleHealthPush' })
    const lastAppleHealthPush = parseInt(lastAppleHealthPushRaw as string)
    const syncThrottlePassed = lastAppleHealthPush < timestamp - appleHealthThrottleLimit
    const lastPushWasInTheFuture = lastAppleHealthPush > timestamp + 1000
    const secondsSinceLastPush = Math.round((timestamp - lastAppleHealthPush) / 1000)

    if (lastAppleHealthPush && !syncThrottlePassed && !lastPushWasInTheFuture) {
      log(
        `Skipping ${serviceProvider} sync – last one was done just ${secondsSinceLastPush} seconds ago. ${
          manualSync ? '(manual sync)' : ''
        }`,
      )
      return
    } else {
      const syncStart = window.performance.now()
      const needsFullScopeSync = !!dateOfBirth
      log(
        `Starting ${serviceProvider} sync – ${secondsSinceLastPush} seconds has passed. ${
          manualSync ? '(manual sync) ' : ''
        }| needsFullScopeSync: ${needsFullScopeSync ? 'YES' : 'NO'} | nonDistanceSync: ${
          nonDistanceSync ? 'YES' : 'NO'
        }`,
      )
      await Preferences.set({ key: 'lastAppleHealthPush', value: String(timestamp) })

      const { health: HealthPlugin } = navigator as any
      const health = isGoogleFit ? GoogleFit : HealthPlugin

      let healthDataTypes = AppleHealthDataTypes
      if (isGoogleFit) {
        healthDataTypes = healthDataTypes.filter((h: any) => !h.iosOnly)

        const userIntegrationScopes = getUserIntegrationScopes(state)
        if (
          nonDistanceSync ||
          !userIntegrationScopes.includes('https://www.googleapis.com/auth/fitness.location.read')
        ) {
          log(`Skipping distance | userIntegrationScopes: ${JSON.stringify(userIntegrationScopes)}`)
          healthDataTypes = healthDataTypes.filter((h: any) => h.activityType !== ActivityType.DISTANCE)
        }
      } else {
        healthDataTypes = healthDataTypes.filter((h: any) => !h.androidOnly)
      }

      // For some, do lean sync (just sync steps and distance).
      if (!needsFullScopeSync) {
        healthDataTypes = healthDataTypes.filter((h: any) => !h.fullScope)
      }

      const userCompanies = getUserCompanies(state)
      const syncActivities = userCompanies && userCompanies.some((uc) => uc.scope?.includes('syncActivities'))
      const syncSleep = userCompanies && userCompanies.some((uc) => uc.scope?.includes('syncSleep'))

      // Without "syncActivities" feature flag, we don't sync exercise time.
      if (!syncActivities) {
        healthDataTypes = healthDataTypes.filter((h: any) => h.activityType !== ActivityType.EXERCISE_MIN)
      }
      // Without "syncSleep" feature flag, we don't sync sleep time.
      if (!syncSleep) {
        healthDataTypes = healthDataTypes.filter((h: any) => h.activityType !== ActivityType.SLEEP_MIN)
      }

      const read = healthDataTypes.map((i) => i.dataType)
      const scope = [{ read }]
      log(`Scope: ${JSON.stringify(scope)}`)

      if (isGoogleFit) {
        console.log('BEFORE GoogleFit.requestAuthorization()')
        // TODO: workouts permission?
        await GoogleFit.requestAuthorization({ scope }).catch((error: any) => {
          console.log(JSON.stringify(error))
          log(`${serviceProvider} sync requestAuthorization error: ${JSON.stringify(error)}`)
          if (isGoogleFit && !nonDistanceSync) {
            dataSync(true, forceLongSync, true)
          }
        })
        console.log('AFTER GoogleFit.connectToGoogleFit()')
      } else {
        await new Promise<void>((resolve, reject) => {
          const authScope = scope
          // Ask for activity permissions
          if (syncActivities && authScope[0].read) {
            authScope[0].read.push('activity')
          }
          health.requestAuthorization(
            authScope,
            () => {
              resolve()
            },
            (error: any) => {
              console.log(JSON.stringify(error))
              log(`${serviceProvider} sync requestAuthorization error: ${JSON.stringify(error)}`)
              if (isGoogleFit && !nonDistanceSync) {
                dataSync(true, forceLongSync, true)
              }
              reject()
            },
          )
        })
      }

      const { lastSyncDatePerActivity }: any = isGoogleFit
        ? await api.activity.getGoogleFitAndroidSyncTimes({ forceLongSync })
        : await api.activity.getAppleHealthSyncTimes({ forceLongSync })

      if (lastSyncDatePerActivity === null) {
        return log(`${serviceProvider} sync DISABLED`)
      }

      document.body.classList.add('is-syncing')

      const combinedActivitiesData = [] as any
      const promises = [] as Promise<string>[]
      for (const { dataType, activityType, aggregated } of healthDataTypes) {
        // We don't need gender and date_of_birth sync anymore. Plus filter out height and weight for now.
        if (!activityType) {
          continue
        }

        const startDate = lastSyncDatePerActivity[activityType as string]
        // If we get no start date from back-end, we shouldn't sync it.
        if (!startDate) {
          continue
        }

        document.body.classList.add('is-syncing')

        // Steps, exercise time, distance and calories.
        if (aggregated) {
          const promiseActivityType = activityType
          const promiseDataType = dataType
          log(`${activityType} query starting | ${serviceProvider}`)
          const indiSyncStart = window.performance.now()

          const handleQueryAggregated = async (result: any) => {
            let entries = (isGoogleFit ? result.activities : result) as AppleHealthDiaryEntry[]

            // Don't include entries with 0 values.
            if (!isGoogleFit) {
              entries = entries.filter((entry: AppleHealthDiaryEntry) => entry.value > 0)
            }

            // Only send the useful data.
            entries = entries.map((entry: AppleHealthDiaryEntry) => {
              return {
                startDate: entry.startDate,
                endDate: entry.endDate,
                //timeOffset: null,
                value: Math.floor(entry.value),
              }
            })

            // For debugging.
            const syncDuration = Math.round(window.performance.now() - indiSyncStart)
            const msg = `${promiseActivityType} query (${entries.length} items) completed in ${syncDuration}ms | ${serviceProvider}`
            log(msg)

            // Push to back-end.
            combinedActivitiesData.push({
              activityType: promiseActivityType,
              diaryEntries: entries,
            })
          }

          if (isGoogleFit) {
            await health
              .queryAggregated({
                dataType: promiseDataType,
                endDate: new Date().getTime(),
                startDate: convertUTCToLocalTime(startDate).getTime(),
                bucket: 'day',
              })
              .then(handleQueryAggregated)
              .catch((error: any) => {
                console.log(error)

                // For debugging.
                const syncDuration = Math.round(window.performance.now() - indiSyncStart)
                log(`${promiseActivityType} query failed in ${syncDuration}ms | ${serviceProvider}`)
              })
          } else {
            promises.push(
              new Promise((resolve, reject) => {
                // TODO: Should they be here (inside the promise) or not?
                /*const promiseActivityType = activityType
                const promiseDataType = dataType
                const msg = `${activityType} query starting | ${serviceProvider}`
                log(msg)
                const indiSyncStart = window.performance.now()*/
                // TODO: What if calories (or HR) query doesn't return anything ?
                health.queryAggregated(
                  {
                    dataType: promiseDataType,
                    endDate: new Date(),
                    startDate: convertUTCToLocalTime(startDate),
                    bucket: 'day',
                  },
                  async (result: any) => {
                    handleQueryAggregated(result)
                    resolve('')
                  },
                  (error: any) => {
                    console.log(error)

                    // For debugging.
                    const syncDuration = Math.round(window.performance.now() - indiSyncStart)
                    log(`${promiseActivityType} query failed in ${syncDuration}ms | ${serviceProvider}`)

                    reject()
                  },
                )
              }),
            )
          }
        }
        // Heart rate, height and weight.
        else {
          const promiseActivityType = activityType
          const promiseDataType = dataType
          log(`${activityType} query starting | ${serviceProvider}`)
          const indiSyncStart = window.performance.now()

          const handleQuery = async (result: any) => {
            let diaryEntries = (isGoogleFit ? result.activities : result) as AppleHealthDiaryEntry[]

            // Only send the useful data.
            diaryEntries = diaryEntries.map((entry: AppleHealthDiaryEntry) => {
              return { startDate: entry.startDate, endDate: entry.endDate, /*timeOffset: null,*/ value: entry.value }
            })

            // For debugging.
            const syncDuration = Math.round(window.performance.now() - indiSyncStart)
            log(
              `${promiseActivityType} query (${diaryEntries.length} items) completed in ${syncDuration}ms | ${serviceProvider}`,
            )

            // Push to back-end.
            combinedActivitiesData.push({
              activityType: promiseActivityType,
              diaryEntries,
            })
          }

          if (isGoogleFit) {
            if (activityType === ActivityType.HEART_RATE) {
              await GoogleFit.heartRateQuery({
                startTime: convertUTCToLocalTime(startDate).getTime(),
                endTime: new Date().getTime(),
              })
                .then(handleQuery)
                .catch((error: any) => {
                  console.log(error)

                  // For debugging.
                  const syncDuration = Math.round(window.performance.now() - indiSyncStart)
                  log(`${promiseActivityType} query failed in ${syncDuration}ms | ${serviceProvider}`)
                })
            } else if (activityType === ActivityType.SLEEP_MIN) {
              await GoogleFit.getSleepActivity({
                startTime: convertUTCToLocalTime(startDate).getTime(),
                endTime: new Date().getTime(),
              })
                .then(handleQuery)
                .catch((error: any) => {
                  console.log(error)

                  // For debugging.
                  const syncDuration = Math.round(window.performance.now() - indiSyncStart)
                  log(`${promiseActivityType} query failed in ${syncDuration}ms | ${serviceProvider}`)
                })
            }
          } else {
            promises.push(
              new Promise((resolve, reject) => {
                HealthPlugin.query(
                  {
                    dataType: promiseDataType,
                    endDate: new Date(),
                    // For HR, keep the same UTC time.
                    startDate: new Date(startDate),
                    limit: promiseDataType === 'heart_rate' ? 300000 : 1,
                  },
                  async (result: any) => {
                    handleQuery(result)
                    resolve('')
                  },
                  (error: any) => {
                    console.log(error)

                    // For debugging.
                    const syncDuration = Math.round(window.performance.now() - indiSyncStart)
                    log(`${promiseActivityType} query failed in ${syncDuration}ms | ${serviceProvider}`)

                    reject()
                  },
                )
              }),
            )
          }
        }
      }

      let activityEntries: any[] = []
      let deletedActivities: any[] = []

      const activityActivityType = ProgressType.ACTIVITY
      const activityStartDate = lastSyncDatePerActivity[activityActivityType]
      if (syncActivities && activityStartDate) {
        log(`${activityActivityType} query starting | ${serviceProvider}`)
        const indiSyncStart = window.performance.now()

        if (isGoogleFit) {
          await health
            .getHistoryActivity({
              startTime: convertUTCToLocalTime(activityStartDate).getTime(),
              endTime: new Date().getTime(),
            })
            .then(async ({ activities }: ActivityContainer) => {
              // Only send the useful data.
              const entries = activities.map((entry: HistoryActivityData) => {
                return {
                  startDate: entry.start,
                  endDate: entry.end,
                  activityType: entry.activity,
                  calories: entry.calories,
                  distance: entry.distance,
                  duration: entry.duration,
                  // Null pointer exception in the backend otherwise...
                  steps: entry.steps || 0,
                  source: entry.source,
                  originalId: entry.id,
                }
              })

              // For debugging.
              const syncDuration = Math.round(window.performance.now() - indiSyncStart)
              const msg = `${activityActivityType} query (${entries.length} items) completed in ${syncDuration}ms | ${serviceProvider}`
              log(msg)

              // Push to back-end.
              activityEntries = entries
            })
            .catch((error: any) => {
              console.log(error)
            })
          /*await health
            .query({
              dataType: 'activity',
              endDate: new Date().getTime(),
              startDate: new Date(startDate).getTime(),
              limit: 1000,
            })
            .then(handleActivityQuery)
            .catch((error: any) => {
              console.log(error)
            })*/
        } else {
          // Existing activities
          promises.push(
            new Promise((resolve, reject) => {
              health.query(
                {
                  dataType: 'activity',
                  endDate: new Date().getTime(),
                  startDate: convertUTCToLocalTime(activityStartDate).getTime(),
                  limit: 1000,
                  includeCalsAndDist: true,
                },
                async (entries: any) => {
                  // console.log('activities', entries)

                  // Only send the useful data.
                  entries = entries.map((entry: any) => {
                    return {
                      startDate: entry.startDate,
                      endDate: entry.endDate,
                      activityType: entry.value,
                      // Missing?
                      calories: entry.calories,
                      // Missing?
                      distance: entry.distance,
                      duration: entry.duration,
                      steps: 0,
                      source: `${entry.sourceBundleId} (${entry.sourceName?.replace(/\u00a0/g, ' ')})`,
                      originalId: entry.id,
                    }
                  })

                  // For debugging.
                  const syncDuration = Math.round(window.performance.now() - indiSyncStart)
                  const msg = `${activityActivityType} query (${entries.length} items) completed in ${syncDuration}ms | ${serviceProvider}`
                  log(msg)

                  // Push to back-end.
                  activityEntries = entries
                  resolve('')
                },
                (error: any) => {
                  console.log(error)
                  reject()
                },
              )
            }),
          )
          // Deleted activities
          promises.push(
            new Promise((resolve, reject) => {
              health.query(
                {
                  dataType: 'deletedActivity',
                },
                async (entries: any) => {
                  console.log('deletedActivities', entries)
                  deletedActivities = entries
                  resolve('')
                },
                (error: any) => {
                  console.log(error)
                  reject()
                },
              )
            }),
          )
        }
      }

      if (!isGoogleFit) {
        await Promise.all(promises)
      }

      // Make sure the longest (HEART_RATE) is last in the list.
      combinedActivitiesData.sort().reverse()

      const queryDuration = Math.round((window.performance.now() - syncStart) / 1000)
      log(`${serviceProvider} queries completed in ${queryDuration} seconds.`)

      if (isGoogleFit) {
        await api.activity.uploadAllGoogleFitActivities({
          googleFitActivities: combinedActivitiesData,
          // @ts-ignore
          activityEntries,
          timeZone: moment.tz.guess(true),
        })
      } else {
        await api.activity.uploadAllAppleHealthActivities({
          appleHealthActivities: combinedActivitiesData,
          // @ts-ignore
          activityEntries,
          // @ts-ignore
          deletedActivities,
          timeZone: moment.tz.guess(true),
        })
      }

      // It's time to update activity view. But let's give backend a second for recalculations.
      setTimeout(() => {
        refreshActivityView()
      }, 250)

      const syncDuration = Math.round((window.performance.now() - syncStart) / 1000)
      log(`${serviceProvider} sync completed in ${syncDuration} seconds.`)

      //setTimeout(() => {
      //document.body.classList.remove('is-syncing')

      const icon = document.querySelector('.btn-sync .button__icon')
      if (icon) {
        icon.addEventListener(
          'animationiteration',
          () => {
            document.body.classList.remove('is-syncing')
          },
          { once: true },
        )
      }
      //}, 500)
    }
  } catch (e: any) {
    console.log('Error: ', e)
    document.body.classList.remove('is-syncing')
  }
}
